"use client";

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type { BlogTocItem } from "@/lib/types";

interface ReaderState {
  toc: BlogTocItem[];
  sectionLabel: string;
  /** 1-based index of the section the reader is currently in. */
  active: number;
  /** How far through the whole document, 0 → 1. */
  progress: number;
  /** How far through the run of sections specifically, 0 → 1. Drives the rail. */
  railProgress: number;
  /**
   * True while the reader is below the title block.
   *
   * The bar reports progress, and a progress indicator sitting at "0% read"
   * over the headline is noise. It slides in on the way down and back out when
   * the reader returns to the top.
   */
  started: boolean;
}

const ReaderContext = createContext<ReaderState | null>(null);

export const useBlogReader = () => {
  const value = useContext(ReaderContext);
  if (!value) {
    throw new Error("useBlogReader must be used inside BlogReaderProvider");
  }
  return value;
};

interface BlogReaderProviderProps {
  toc: BlogTocItem[];
  sectionLabel: string;
  children: ReactNode;
}

/**
 * The one scroll loop on a post page.
 *
 * The sticky bar, the desktop index rail and the mobile sheet all need the same
 * two numbers, and three components each running their own listener would read
 * layout three times per frame. They share this instead.
 *
 * The active section is found by measuring rather than by IntersectionObserver.
 * An observer only fires when a boundary is crossed, so a reader sitting in the
 * middle of a long section — which is most of the time — gets no event at all,
 * and a resize or an image settling can leave the highlight on the wrong one.
 * One measurement per frame is cheap and always correct.
 */
const BlogReaderProvider = ({
  toc,
  sectionLabel,
  children,
}: BlogReaderProviderProps) => {
  const [active, setActive] = useState(1);
  const [progress, setProgress] = useState(0);
  const [railProgress, setRailProgress] = useState(0);
  const [started, setStarted] = useState(false);

  // Mirrors of the state, so the frame can bail out before calling a setter
  // and re-rendering three components for a value that has not changed.
  const activeRef = useRef(1);
  const progressRef = useRef(0);
  const railRef = useRef(0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (toc.length === 0) return;

    let frame = 0;

    const read = () => {
      frame = 0;

      const doc = document.scrollingElement ?? document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      const next = max > 0 ? Math.min(Math.max(doc.scrollTop / max, 0), 1) : 0;

      // Roughly one swipe, or the height of the title block — far enough that
      // the bar is a response to reading rather than part of the page header.
      // Two thresholds rather than one: a single boundary makes the bar flicker
      // in and out while a finger rests near it.
      const show = startedRef.current
        ? doc.scrollTop > 160
        : doc.scrollTop > 260;

      if (show !== startedRef.current) {
        startedRef.current = show;
        setStarted(show);
      }

      // One decimal place of a percentage is all the bar and the "% read"
      // label can show; rounding here is what keeps this from re-rendering on
      // every single frame of a scroll.
      if (Math.abs(next - progressRef.current) > 0.002) {
        progressRef.current = next;
        setProgress(next);
      }

      const sections = toc
        .map((item) => document.getElementById(item.id))
        .filter((el): el is HTMLElement => Boolean(el));

      if (sections.length === 0) return;

      const line = window.innerHeight * 0.4;

      // The last section whose top has passed the reading line. Before the
      // first one does, the reader is still in the lede, which belongs to
      // section one as far as the index is concerned.
      let current = 1;
      sections.forEach((section, i) => {
        if (section.getBoundingClientRect().top <= line) current = i + 1;
      });

      if (current !== activeRef.current) {
        activeRef.current = current;
        setActive(current);
      }

      const first = sections[0].getBoundingClientRect().top;
      const last = sections[sections.length - 1].getBoundingClientRect().bottom;
      const span = last - first;
      const rail =
        span > 0 ? Math.min(Math.max((line - first) / span, 0), 1) : 0;

      if (Math.abs(rail - railRef.current) > 0.004) {
        railRef.current = rail;
        setRailProgress(rail);
      }
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(read);
    };

    // `capture` because lenis drives the page from a scroll container, and a
    // listener bound to window alone misses the phase on some setups.
    document.addEventListener("scroll", onScroll, {
      passive: true,
      capture: true,
    });
    window.addEventListener("resize", onScroll, { passive: true });
    onScroll();

    return () => {
      if (frame) cancelAnimationFrame(frame);
      document.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onScroll);
    };
  }, [toc]);

  return (
    <ReaderContext.Provider
      value={{ toc, sectionLabel, active, progress, railProgress, started }}
    >
      {children}
    </ReaderContext.Provider>
  );
};

export default BlogReaderProvider;
